import { useState, useEffect, Suspense } from 'react';
import axios from 'axios';
import {
  Table,
  Input,
  Button,
  Tag,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Spin,
  Modal,
  Descriptions,
  Tooltip,
  Divider,
  Empty,
  message,
  Alert
} from 'antd';
import {
  SearchOutlined,
  DollarOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { supabase } from '../lib/supabase';
import { MoneybirdInvoice } from '../types';
import { useAuth } from '../contexts/AuthContext';
import ErrorBoundary from '../components/ErrorBoundary';
import React from 'react';

const { Title, Text } = Typography;
const { Search } = Input;

// Define status colors based on the Moneybird state field
const statusColors: Record<string, string> = {
  open: 'warning',
  paid: 'success',
  late: 'error',
  draft: 'default',
  scheduled: 'processing',
  pending_payment: 'warning',
};

// Dutch translations for the invoice states
const statusLabels: Record<string, string> = {
  open: 'Open',
  paid: 'Betaald',
  late: 'Verlopen',
  draft: 'Concept',
  scheduled: 'Gepland',
  pending_payment: 'In behandeling',
};

// Create a fallback renderer for the whole page
const FallbackComponent = () => {
  return (
    <div>
      <h1>Facturen</h1>
      <div style={{padding: 20}}>
        <p>The invoice component couldn't be loaded. Please try again later.</p>
        <p>If this issue persists, please contact support.</p>
        <Button type="primary" onClick={() => window.location.reload()}>
          Reload Page
        </Button>
      </div>
    </div>
  );
};

// Fixed error boundary with proper TypeScript types
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

// Create an error boundary component
export class ErrorBoundaryFallback extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <FallbackComponent />;
    }

    return this.props.children;
  }
}

// Export Moneybird Facturen with error boundary and suspense
export default function MoneybirdFacturenWithErrorHandling() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<div style={{padding: 20}}><Spin size="large" tip="Facturen laden..." /></div>}>
        <MoneybirdInvoiceContent />
      </Suspense>
    </ErrorBoundary>
  );
}

// The actual invoice component
function MoneybirdInvoiceContent() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<MoneybirdInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredInvoices, setFilteredInvoices] = useState<MoneybirdInvoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<MoneybirdInvoice | null>(null);
  const [companyData, setCompanyData] = useState<any | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Add global error handler
  useEffect(() => {
    const handleErrors = (event: ErrorEvent) => {
      console.error('Global error caught:', event.error);
    };

    window.addEventListener('error', handleErrors);
    return () => window.removeEventListener('error', handleErrors);
  }, []);

  useEffect(() => {
    if (user) {
      fetchCompanyData();
    } else {
      console.log('No user found, cannot fetch company data');
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (companyData?.moneybird_contact_id) {
      console.log('Company data loaded, Moneybird contact ID:', companyData.moneybird_contact_id);
      fetchInvoices(companyData.moneybird_contact_id);
    } else if (companyData) {
      console.log('Company data loaded but no Moneybird contact ID found');
      setLoading(false);
    }
  }, [companyData]);

  useEffect(() => {
    // Filter invoices when search query changes
    if (!invoices.length) return;
    
    const filtered = invoices.filter(invoice => {
      const searchLower = searchQuery.toLowerCase();
      return (
        invoice.invoice_id?.toLowerCase().includes(searchLower) ||
        invoice.reference?.toLowerCase().includes(searchLower) ||
        invoice.state?.toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredInvoices(filtered);
  }, [searchQuery, invoices]);

  const fetchCompanyData = async () => {
    try {
      console.log('Fetching company data for user ID:', user?.id);
      const { data, error } = await supabase
        .from('companies')
        .select('id, moneybird_contact_id, bedrijfsnaam')
        .eq('user_id', user?.id)
        .single();

      if (error) {
        console.error('Error fetching company data:', error);
        return;
      }
      
      console.log('Company data fetched:', data);
      setCompanyData(data);
      
      if (!data?.moneybird_contact_id) {
        console.log('Geen Moneybird contact ID gevonden voor dit bedrijf');
      }
    } catch (error: any) {
      console.error('Exception in fetchCompanyData:', error);
    }
  };

  const fetchInvoices = async (contactId: string) => {
    setLoading(true);
    try {
      console.log('Fetching invoices for contact ID:', contactId);
      console.log('Current hostname:', window.location.hostname);
      
      // Set up API URLs for both local and production environments
      const isLocalDev = window.location.hostname === 'localhost';
      
      // For local development, use the Express server endpoints
      // For production, use the serverless function endpoints
      let testApiUrl, invoicesApiUrl;
      
      if (isLocalDev) {
        // Local development - use Express server
        const apiBaseUrl = 'http://localhost:3001';
        testApiUrl = `${apiBaseUrl}/api/moneybird/test`;
        invoicesApiUrl = `${apiBaseUrl}/api/moneybird/invoices`;
      } else {
        // Production - use serverless functions
        const appUrl = window.location.origin;
        testApiUrl = `${appUrl}/api/moneybird-test`;
        invoicesApiUrl = `${appUrl}/api/moneybird-invoices`;
      }
      
      console.log('Test API URL:', testApiUrl);
      console.log('Invoices API URL:', invoicesApiUrl, 'with params:', { contact_id: contactId });
      
      // Set a longer timeout for the request
      const timeoutMs = 15000; // 15 seconds
      
      try {
        // First check if we can connect to the API at all
        console.log('Testing API connectivity...');
        const testResponse = await axios.get(testApiUrl, { 
          timeout: timeoutMs / 3
        }).catch(e => {
          console.log('API test endpoint failed:', e.message);
          if (e.response) {
            console.log('Test response status:', e.response.status);
            console.log('Test response content type:', e.response.headers['content-type']);
            if (typeof e.response.data === 'string') {
              console.log('Test response preview:', e.response.data.substring(0, 200));
            }
          }
          return null;
        });
        
        if (testResponse) {
          console.log('API test endpoint response:', testResponse.data);
        }
        
        // Now try the actual invoices endpoint
        const response = await axios.get(invoicesApiUrl, {
          params: { contact_id: contactId },
          timeout: timeoutMs
        });
        
        console.log('API response status:', response.status);
        console.log('API response content type:', response.headers['content-type']);
        
        // Check if response is HTML instead of JSON
        if (response.headers['content-type'] && 
            response.headers['content-type'].includes('text/html')) {
          console.error('Received HTML response instead of JSON');
          
          // Log the first part of the HTML response for debugging
          if (typeof response.data === 'string') {
            console.error('HTML response preview (first 300 characters):', response.data.substring(0, 300));
          } else {
            console.error('Response data type:', typeof response.data);
          }
          
          setApiError('Authenticatiefout bij het verbinden met Moneybird. Probeer later opnieuw of neem contact op met support.');
          setInvoices([]);
          setFilteredInvoices([]);
          return;
        }
        
        // Check if the response contains an error flag
        if (response.data && response.data.error === true) {
          console.error('API returned an error:', response.data.message);
          
          // Set the error message from the API response
          setApiError(response.data.message || 'Er is een fout opgetreden bij het ophalen van facturen');
          
          // If there's an invoices array property, use it (might be empty)
          if (response.data.invoices && Array.isArray(response.data.invoices)) {
            setInvoices(response.data.invoices);
            setFilteredInvoices(response.data.invoices);
          } else {
            setInvoices([]);
            setFilteredInvoices([]);
          }
          return;
        }
        
        // Clear any previous errors since we have a successful response
        setApiError(null);
        
        // Examine the response data in more detail
        if (!response.data) {
          console.log('API response data is empty or null');
          setInvoices([]);
          setFilteredInvoices([]);
          return;
        }
        
        let invoiceData = response.data;
        
        // Check if the data is in an unexpected format
        if (typeof invoiceData === 'string') {
          try {
            // Check if it's an HTML response before trying to parse as JSON
            if (invoiceData.trim().toLowerCase().startsWith('<!doctype') || 
                invoiceData.trim().toLowerCase().startsWith('<html')) {
              console.error('Received HTML string instead of JSON');
              setApiError('Authenticatiefout bij het verbinden met Moneybird. Probeer later opnieuw of neem contact op met support.');
              setInvoices([]);
              setFilteredInvoices([]);
              return;
            }
            
            // Try to parse it if it's a JSON string
            invoiceData = JSON.parse(invoiceData);
            console.log('Parsed string response into object:', invoiceData);
          } catch (error: any) {
            console.error('Failed to parse response string as JSON:', error);
            setApiError('Ongeldig antwoord ontvangen van de server. Probeer het later opnieuw.');
            setInvoices([]);
            setFilteredInvoices([]);
            return;
          }
        }
        
        // Check if the data is a JSON object with an invoices property
        if (invoiceData && typeof invoiceData === 'object' && !Array.isArray(invoiceData)) {
          if (invoiceData.invoices && Array.isArray(invoiceData.invoices)) {
            invoiceData = invoiceData.invoices;
            console.log('Extracted invoices array from response object');
          } else {
            console.log('Response is an object but does not contain an invoices array');
            console.log('Response structure:', Object.keys(invoiceData));
            invoiceData = [];
          }
        }
        
        // Final check to ensure we have an array
        if (!Array.isArray(invoiceData)) {
          console.log('API response data is not an array, actual type:', typeof invoiceData);
          console.log('Data preview:', JSON.stringify(invoiceData).substring(0, 200) + '...');
          setInvoices([]);
          setFilteredInvoices([]);
          return;
        }
        
        console.log('API response data length:', invoiceData.length);
        
        if (invoiceData.length > 0) {
          const firstInvoice = invoiceData[0];
          console.log('First invoice price data:', {
            id: firstInvoice.id,
            invoice_id: firstInvoice.invoice_id,
            total_price_excl_tax: firstInvoice.total_price_excl_tax,
            total_price_incl_tax: firstInvoice.total_price_incl_tax
          });
        } else {
          console.log('No invoice data returned or empty array');
        }
        
        setInvoices(invoiceData);
        setFilteredInvoices(invoiceData);
      } catch (apiError: any) {
        console.error('Error fetching from API, details:', apiError);
        
        // Set a user-friendly error message based on the error
        let errorMessage = 'Er is een fout opgetreden bij het ophalen van facturen';
        
        if (apiError.response) {
          if (apiError.response.status === 401 || apiError.response.status === 403) {
            errorMessage = 'Authenticatiefout bij het verbinden met Moneybird. Probeer later opnieuw.';
          } else if (apiError.response.status === 429) {
            errorMessage = 'Te veel verzoeken naar Moneybird. Probeer het over een paar minuten opnieuw.';
          } else if (apiError.response.status >= 500) {
            errorMessage = 'Moneybird server is momenteel niet beschikbaar. Probeer het later opnieuw.';
          }
          
          // Check if we received HTML instead of JSON (auth issue)
          if (apiError.response.headers && 
              apiError.response.headers['content-type'] &&
              apiError.response.headers['content-type'].includes('text/html')) {
            errorMessage = 'Authenticatiefout bij het verbinden met Moneybird. Probeer later opnieuw of neem contact op met support.';
          }
        } else if (apiError.request) {
          // Request was made but no response received (network error)
          errorMessage = 'Kon geen verbinding maken met de server. Controleer uw internetverbinding.';
        } else if (apiError.message && apiError.message.includes('timeout')) {
          errorMessage = 'Verzoek duurde te lang. Probeer het later opnieuw.';
        }
        
        setApiError(errorMessage);
        setInvoices([]);
        setFilteredInvoices([]);
      }
    } catch (error: any) {
      console.error('Error in invoice fetching process:', error);
      setApiError('Er is een onverwachte fout opgetreden. Probeer het later opnieuw.');
      setInvoices([]);
      setFilteredInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: string | number) => {
    // If price is undefined or null, return a default value
    if (price === undefined || price === null) {
      return '€0,00';
    }
    
    // First ensure we have a number to work with
    let numPrice: number;
    
    if (typeof price === 'string') {
      // Remove any currency symbols or thousand separators
      const cleanedString = price.replace(/[^0-9.,]/g, '').replace(',', '.');
      numPrice = parseFloat(cleanedString);
    } else {
      numPrice = price;
    }
    
    // Check if the number is valid
    if (isNaN(numPrice)) {
      console.warn('Invalid price value:', price);
      return '€0,00';
    }
    
    // Format the number as currency
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(numPrice);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getDaysUntilDue = (dueDate: string) => {
    if (!dueDate) return null;
    
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const openInvoicePaymentPage = (invoice: MoneybirdInvoice) => {
    try {
      // Use the payment_url field from the Moneybird invoice
      if (invoice.url) {
        window.open(invoice.url, '_blank');
      } else {
        console.error('No payment URL available for this invoice');
      }
    } catch (error) {
      console.error('Error opening invoice payment page:', error);
    }
  };

  const getStatusTag = (state: string) => {
    return (
      <Tag color={statusColors[state] || 'default'}>
        {statusLabels[state] || state}
      </Tag>
    );
  };

  // Define table columns for main invoice list
  const columns = [
    {
      title: 'Factuurnummer',
      dataIndex: 'invoice_id',
      key: 'invoice_id',
    },
    {
      title: 'Datum',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Vervaldatum',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date: string, record: MoneybirdInvoice) => (
        <>
          {formatDate(date)}
          {record.state === 'open' && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {getDaysUntilDue(date)} dagen resterend
              </Text>
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      render: (state: string) => getStatusTag(state),
    },
    {
      title: 'Bedrag (incl. BTW)',
      dataIndex: 'total_price_incl_tax',
      key: 'total_price_incl_tax',
      align: 'right' as const,
      render: (price: string) => formatPrice(price),
    },
    {
      title: 'Acties',
      key: 'actions',
      align: 'right' as const,
      render: (_: any, record: MoneybirdInvoice) => (
        <Space size="small">
          <Tooltip title="Betaal factuur in Moneybird">
            <Button 
              type="primary" 
              icon={<DollarOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openInvoicePaymentPage(record);
              }}
            >
              Betaal
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Define table columns for invoice details
  const detailColumns = [
    {
      title: 'Omschrijving',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Aantal',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
    },
    {
      title: 'Prijs (excl. BTW)',
      dataIndex: 'price',
      key: 'price',
      align: 'right' as const,
      render: (price: string) => formatPrice(price),
    },
    {
      title: 'Totaal',
      key: 'total',
      align: 'right' as const,
      render: (_: unknown, record: any) => formatPrice(parseFloat(record.price) * parseFloat(record.amount)),
    },
  ];

  // Add a refresh function
  const refreshData = () => {
    if (companyData?.moneybird_contact_id) {
      fetchInvoices(companyData.moneybird_contact_id);
    } else {
      fetchCompanyData();
    }
  };

  // Render main component content based on data state
  const renderContent = () => {
    // Show error if no Moneybird contact ID is found
    if (!companyData?.moneybird_contact_id) {
      return (
        <Card>
          <Empty
            description={
              <div style={{ textAlign: 'center' }}>
                <p>Geen Moneybird contactID gevonden voor uw bedrijf.</p>
                <p style={{ color: '#888' }}>Neem contact op met support om dit in te stellen.</p>
                <Button 
                  type="primary" 
                  onClick={refreshData} 
                  loading={loading}
                  style={{ marginTop: 16 }}
                >
                  <ReloadOutlined /> Probeer opnieuw
                </Button>
              </div>
            }
          />
        </Card>
      );
    }

    // Show the invoices table
    return (
      <Card title="Facturen">
        {apiError && (
          <Alert
            message="Fout bij het ophalen van facturen"
            description={apiError}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={refreshData} type="primary">
                <ReloadOutlined /> Opnieuw proberen
              </Button>
            }
          />
        )}
        
        <Input.Search
          placeholder="Zoek op factuurnummer of kenmerk"
          allowClear
          onSearch={value => setSearchQuery(value)}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ marginBottom: 16, width: 300 }}
        />
        
        <Table
          dataSource={filteredInvoices || []}
          columns={columns || []}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ 
            emptyText: 'Geen facturen gevonden'
          }}
          onRow={(record) => ({
            onClick: () => {
              setSelectedInvoice(record);
              setModalVisible(true);
            },
            style: { cursor: 'pointer' }
          })}
        />
        
        {/* Invoice detail modal */}
        <Modal
          title={`Factuur ${selectedInvoice?.invoice_id || ''}`}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          width={800}
          footer={[
            <Button key="close" onClick={() => setModalVisible(false)}>
              Sluiten
            </Button>,
            <Button
              key="pay"
              type="primary"
              icon={<DollarOutlined />}
              onClick={() => selectedInvoice && openInvoicePaymentPage(selectedInvoice)}
            >
              Betaal in Moneybird
            </Button>,
          ]}
        >
          {selectedInvoice && (
            <>
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Title level={5} style={{ marginTop: 0 }}>Factuur Informatie</Title>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Factuurdatum">
                      {formatDate(selectedInvoice.invoice_date)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Vervaldatum">
                      {formatDate(selectedInvoice.due_date)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Betaaldatum">
                      {selectedInvoice.paid_at ? formatDate(selectedInvoice.paid_at) : 'Nog niet betaald'}
                    </Descriptions.Item>
                    {selectedInvoice.reference && (
                      <Descriptions.Item label="Referentie">
                        {selectedInvoice.reference}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Col>
                <Col xs={24} md={12}>
                  <Title level={5} style={{ marginTop: 0 }}>Bedragen</Title>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Totaal (excl. BTW)">
                      {formatPrice(selectedInvoice.total_price_excl_tax)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Totaal (incl. BTW)">
                      {formatPrice(selectedInvoice.total_price_incl_tax)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      {getStatusTag(selectedInvoice.state)}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
              
              <Divider />
              
              <Title level={5}>Factuurregels</Title>
              <Table
                dataSource={selectedInvoice.details || []}
                columns={detailColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />
            </>
          )}
        </Modal>
      </Card>
    );
  };

  /* Main component return */
  return (
    <div style={{ padding: '20px' }}>
      {renderContent()}
    </div>
  );
} 